import { Route, Routes, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import Guard from './components/Guard';
import LoginPage from './pages/LoginPage';
import MfaChallengePage from './pages/MfaChallengePage';
import SetPasswordPage from './pages/SetPasswordPage';
import CommunityDashboardPage from './pages/CommunityDashboardPage';
import CommunityPartnersPage from './pages/CommunityPartnersPage';
import CommunityChatPage from './pages/CommunityChatPage';
import CommunitySiteConfigPage from './pages/CommunitySiteConfigPage';
import CommunityWrapperPage from './pages/CommunityWrapperPage';
import VolunteersPage from './pages/VolunteersPage';
import VolunteeringActivitiesPage from './pages/VolunteeringActivitiesPage';
import VolunteeringStaffingPage from './pages/VolunteeringStaffingPage';
import VolunteeringWrapperPage from './pages/VolunteeringWrapperPage';
import DonationsPage from './pages/DonationsPage';
import DonationsTransactionsPage from './pages/DonationsTransactionsPage';
import DonationsWrapperPage from './pages/DonationsWrapperPage';
import CampaignsPage from './pages/CampaignsPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import PaymentMethodsWrapperPage from './pages/PaymentMethodsWrapperPage';
import TeamPage from './pages/TeamPage';
import HomePage from './pages/HomePage';
import HomeSlidesPage from './pages/HomeSlidesPage';
import HomeNewsPage from './pages/HomeNewsPage';
import HomeCasesPage from './pages/HomeCasesPage';
import VolunteerPortalPage from './pages/VolunteerPortalPage';
import SecurityPage from './pages/SecurityPage';
import { AsyncConfirmSlot } from './components/ConfirmDialog';
import { useAuth } from './lib/auth';

const isSetPasswordRoute = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/set-password');

function landingRoute(permissions: ReturnType<typeof useAuth>['permissions']) {
  if (permissions.canHome) return '/home/slides';
  if (permissions.canDonations) return '/donations/reports';
  if (permissions.canPaymentMethods) return '/payment-methods/delivery';
  if (permissions.canVolunteering || permissions.canViewVolunteering) return '/volunteering/volunteers';
  if (permissions.canCommunity) return '/community/reports';
  if (permissions.canChat) return '/chat';
  return '/no-access';
}

function NoAccessCard({ email }: { email?: string | null }) {
  return (
    <div className="center">
      <div className="login-card">
        <h1>مفيش صلاحيات لسه</h1>
        <p>
          الحساب <strong>{email}</strong> مضاف كأدمن بس مفيش أي صلاحية على قسم لسه.
          كلم الـ super admin يفتحلك قسم واحد على الأقل من صفحة "فريق الإدارة".
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const { session, isAdmin, isVolunteer, loading, permissions, mfaRequired, mfaChecked } = useAuth();

  if (isSetPasswordRoute()) return <SetPasswordPage />;
  if (loading) return <div className="center">جاري التحميل...</div>;
  if (!session) return <LoginPage />;
  // Post-password MFA gate: if the session is aal1 but a verified
  // TOTP factor exists, block the dashboard until the code is
  // entered. mfaChecked=false means the AAL probe is still in flight —
  // we render a quiet spinner rather than flash the dashboard.
  if (!mfaChecked) return <div className="center">جاري التحقق...</div>;
  if (mfaRequired) return <MfaChallengePage />;
  if (isAdmin === null || isVolunteer === null) {
    return <div className="center">التحقق من الصلاحيات...</div>;
  }
  if (!isAdmin && isVolunteer) return <VolunteerPortalPage />;
  if (!isAdmin) {
    return (
      <div className="center">
        <div className="login-card">
          <div className="login-brand">
            <h1>غير مصرح لك</h1>
            <p>
              الحساب <strong>{session.user.email}</strong> مش مضاف في قائمة الأدمن
              ولا في قائمة المتطوعين. كلم مدير النظام يضيفك.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Structure: each top-level sidebar entry now maps to a wrapper page
  // that shows internal tabs and routes to child pages via <Outlet />.
  // Chat and Team stay as standalone top-level entries.
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Navigate to={landingRoute(permissions)} replace />} />
          <Route path="no-access" element={<NoAccessCard email={session.user.email} />} />

          <Route path="home" element={<Guard need="canHome"><HomePage /></Guard>}>
            <Route index element={<Navigate to="/home/slides" replace />} />
            <Route path="slides" element={<HomeSlidesPage />} />
            <Route path="news" element={<HomeNewsPage />} />
            <Route path="cases" element={<HomeCasesPage />} />
          </Route>

          <Route path="donations" element={<Guard need="canDonations"><DonationsWrapperPage /></Guard>}>
            <Route index element={<Navigate to="/donations/reports" replace />} />
            <Route path="reports" element={<DonationsPage />} />
            <Route path="management" element={<CampaignsPage />} />
            <Route path="transactions" element={<DonationsTransactionsPage />} />
          </Route>

          <Route path="payment-methods" element={<Guard need="canPaymentMethods"><PaymentMethodsWrapperPage /></Guard>}>
            <Route index element={<Navigate to="/payment-methods/delivery" replace />} />
            <Route path="delivery" element={<PaymentMethodsPage defaultTab="delivery" />} />
            <Route path="electronic" element={<PaymentMethodsPage defaultTab="electronic" />} />
            <Route path="banks" element={<PaymentMethodsPage defaultTab="bank" />} />
          </Route>

          <Route path="community" element={<Guard need="canCommunity"><CommunityWrapperPage /></Guard>}>
            <Route index element={<Navigate to="/community/reports" replace />} />
            <Route path="reports" element={<CommunityDashboardPage />} />
            <Route path="partners" element={<CommunityPartnersPage />} />
            <Route path="site-config" element={<CommunitySiteConfigPage />} />
          </Route>

          <Route path="chat" element={<Guard need="canChat"><CommunityChatPage /></Guard>} />

          <Route path="volunteering" element={<Guard need="canViewVolunteering"><VolunteeringWrapperPage /></Guard>}>
            <Route index element={<Navigate to="/volunteering/volunteers" replace />} />
            <Route path="volunteers" element={<VolunteersPage />} />
            <Route path="coordinator" element={<Guard need="canApproveVolunteeringCoordinator"><VolunteersPage filterQueue="coordinator" /></Guard>} />
            <Route path="hr" element={<Guard need="canApproveVolunteeringHr"><VolunteersPage filterQueue="hr" /></Guard>} />
            <Route path="activities" element={<Guard need="canVolunteering"><VolunteeringActivitiesPage /></Guard>} />
            <Route path="staffing" element={<Guard need="canVolunteering"><VolunteeringStaffingPage /></Guard>} />
          </Route>

          <Route path="team" element={<Guard need="super"><TeamPage /></Guard>} />
          <Route path="security" element={<SecurityPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <AsyncConfirmSlot />
    </>
  );
}
